/**
 * A jaw for avatars that have none (2026-10-05, the user's request: lip movement for Luma and
 * for everyone else). The Mixamo characters carry no face rig, so this adds one bone under
 * the head, hinged in front of the ears, and moves the chin and lower lip onto it: vertices
 * mostly on the head, in front of the hinge and below the lip line, weighted by how far
 * below and in front they are, so the lower face drops and the skin stretches between.
 *
 * The lip line is found on the face itself: the nose tip is the most forward point of the
 * face's centre line, and on all six built-ins the seam between the lips is 3.6-4.0 cm below
 * it (measured from the centre-line depth profile). A fixed proportion from the bones missed
 * by a centimetre on some faces, which stretched the chin instead of parting the lips.
 * Geometry is shared between clones of an avatar, so the weights are written once per
 * geometry; every clone gets its own bone at the same skeleton index.
 */
import * as THREE from 'three';

export interface Jaw {
  /** 0 closed .. 1 fully open (about 7 degrees: speech, not a shout). */
  set(open: number): void;
  /** The added bone; absent for faces that open with a blendshape (`face.ts`). */
  readonly bone?: THREE.Bone;
}

const MAX_OPEN = 0.13;
/** Eye height as a share of head bone to head top (the fallback, and the hinge). */
const EYE_SHARE = 0.435;
const LIP_BELOW_EYES = 0.07;
/** The seam between the lips, below the nose tip. */
const SEAM_BELOW_NOSE = 0.034;
/** The hinge: a little below eye level, at the head bone's depth. */
const HINGE_BELOW_EYES = 0.035;

const smooth = (e0: number, e1: number, x: number) => {
  const t = THREE.MathUtils.clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

/**
 * The seam between the lips of each built-in character in the bind pose (metres), read off
 * the full-detail faces by `findLipSeam` (2026-10-06). The low-detail copies share the
 * skeleton and proportions but their coarse lips give no clean crease (avatar-06's lod1 read
 * a centimetre high), so both detail levels use these.
 */
export const LIP_LINE: Readonly<Record<string, number>> = {
  'avatar-01': 1.564,
  'avatar-02': 1.5617,
  'avatar-03': 1.5579,
  'avatar-04': 1.582,
  'avatar-05': 1.5801,
  'avatar-06': 1.5796,
};

export interface JawOptions {
  /** The lip seam's height in the bind pose; found on the face when absent. */
  lipY?: number;
}

export function addJaw(root: THREE.Object3D, options: JawOptions = {}): Jaw | undefined {
  const meshes: THREE.SkinnedMesh[] = [];
  root.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) meshes.push(o as THREE.SkinnedMesh);
  });
  const first = meshes.find((m) => m.skeleton.bones.some((b) => /Head$/.test(b.name)));
  if (!first) return undefined;
  const bones = first.skeleton.bones;
  const headIndex = bones.findIndex((b) => /Head$/.test(b.name));
  const topIndex = bones.findIndex((b) => /HeadTop_End$/.test(b.name));
  const head = bones[headIndex]!;
  const headBind = first.skeleton.boneInverses[headIndex]!.clone().invert();
  const headPos = new THREE.Vector3().setFromMatrixPosition(headBind);
  const topPos =
    topIndex >= 0
      ? new THREE.Vector3().setFromMatrixPosition(
          first.skeleton.boneInverses[topIndex]!.clone().invert(),
        )
      : headPos.clone().add(new THREE.Vector3(0, 0.22, 0.05));
  const eyeY = headPos.y + (topPos.y - headPos.y) * EYE_SHARE;
  const lipY = options.lipY ?? findLipLine(meshes, head, headPos, eyeY);
  const hinge = new THREE.Vector3(0, eyeY - HINGE_BELOW_EYES, headPos.z);

  // The bone, in the head's frame, and its bind inverse.
  const jaw = new THREE.Bone();
  jaw.name = 'clinspace:Jaw';
  const local = hinge.clone().applyMatrix4(first.skeleton.boneInverses[headIndex]!);
  jaw.position.copy(local);
  head.add(jaw);
  const jawBind = headBind.clone().multiply(new THREE.Matrix4().makeTranslation(local));
  const jawInverse = jawBind.clone().invert();
  // Opening turns about the bind pose's sideways axis (+x: the chin goes down and back).
  const axis = new THREE.Vector3(1, 0, 0).transformDirection(jawInverse).normalize();
  const rest = jaw.quaternion.clone();

  const skeletons = new Map<THREE.Skeleton, THREE.Skeleton>();
  for (const mesh of meshes) {
    const old = mesh.skeleton;
    const hi = old.bones.indexOf(head);
    if (hi < 0) continue;
    let next = skeletons.get(old);
    if (!next) {
      next = new THREE.Skeleton([...old.bones, jaw], [...old.boneInverses, jawInverse]);
      skeletons.set(old, next);
    }
    const jawIndex = old.bones.length;
    weightJaw(mesh, hi, jawIndex, hinge, lipY);
    mesh.bind(next, mesh.bindMatrix);
  }

  const q = new THREE.Quaternion();
  return {
    bone: jaw,
    set(open: number) {
      const o = THREE.MathUtils.clamp(open, 0, 1);
      q.setFromAxisAngle(axis, o * MAX_OPEN);
      jaw.quaternion.copy(rest).multiply(q);
    },
  };
}

/** The lip seam found on the face: below the nose, else a share of the head below the eyes. */
function findLipLine(
  meshes: THREE.SkinnedMesh[],
  head: THREE.Bone,
  headPos: THREE.Vector3,
  eyeY: number,
): number {
  const noseY = findNoseTip(meshes, head, headPos);
  if (noseY === undefined) return eyeY - LIP_BELOW_EYES;
  return findLipSeam(meshes, head, headPos, noseY) ?? noseY - SEAM_BELOW_NOSE;
}

/**
 * The height of the nose tip in the bind pose: the most forward head vertex near the face's
 * centre line, between just above the head bone and the eyes.
 */
function findNoseTip(
  meshes: THREE.SkinnedMesh[],
  head: THREE.Bone,
  headPos: THREE.Vector3,
): number | undefined {
  let best: { y: number; z: number } | undefined;
  const v = new THREE.Vector3();
  for (const mesh of meshes) {
    const hi = mesh.skeleton.bones.indexOf(head);
    if (hi < 0) continue;
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    const si = mesh.geometry.getAttribute('skinIndex') as THREE.BufferAttribute;
    const sw = mesh.geometry.getAttribute('skinWeight') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      let w = 0;
      for (let k = 0; k < 4; k++) if (si.getComponent(i, k) === hi) w += sw.getComponent(i, k);
      if (w < 0.5) continue;
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.bindMatrix);
      if (Math.abs(v.x - headPos.x) > 0.012) continue;
      if (v.y < headPos.y + 0.02 || v.y > headPos.y + 0.1) continue;
      if (!best || v.z > best.z) best = { y: v.y, z: v.z };
    }
  }
  return best?.y;
}

/**
 * The seam between the lips, read off the face (2026-10-06): down the centre line below the
 * nose, the upper lip bulges forward, the seam is the deepest point, and the lower lip bulges
 * again. On Luma's full-detail face the seam is 3.0 cm below the nose tip; the fixed 3.4 cm
 * put the hinge line through the middle of her lower lip, which then stretched while the
 * lips stayed shut. Undefined when the profile has no clear crease (a coarse low-detail
 * face); the fixed offset is the fallback.
 */
function findLipSeam(
  meshes: THREE.SkinnedMesh[],
  head: THREE.Bone,
  headPos: THREE.Vector3,
  noseY: number,
): number | undefined {
  // The most forward point per millimetre of height, on the centre line.
  const depth = new Map<number, number>();
  const v = new THREE.Vector3();
  for (const mesh of meshes) {
    const hi = mesh.skeleton.bones.indexOf(head);
    if (hi < 0) continue;
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.bindMatrix);
      if (Math.abs(v.x - headPos.x) > 0.006) continue;
      if (v.y > noseY - 0.01 || v.y < noseY - 0.05) continue;
      const mm = Math.round(v.y * 1000);
      depth.set(mm, Math.max(depth.get(mm) ?? -Infinity, v.z));
    }
  }
  const rows = [...depth].sort((a, b) => b[0] - a[0]);
  const peak = (from: number, to: number) => {
    let best: [number, number] | undefined;
    for (const r of rows) if (r[0] <= from && r[0] >= to && (!best || r[1] > best[1])) best = r;
    return best;
  };
  const nose = Math.round(noseY * 1000);
  const upper = peak(nose - 15, nose - 35);
  if (!upper) return undefined;
  const lower = peak(upper[0] - 5, upper[0] - 20);
  if (!lower) return undefined;
  // The crease is a few millimetres behind the lips; a row much deeper than that holds only
  // the inside of the mouth, where the closed lips curl back, and is not the face's surface.
  let seam: [number, number] | undefined;
  for (const r of rows)
    if (r[0] < upper[0] && r[0] > lower[0] && lower[1] - r[1] < 0.006 && (!seam || r[1] < seam[1]))
      seam = r;
  // A crease at least a millimetre deep behind both lips, or it is not a seam.
  if (!seam || upper[1] - seam[1] < 0.001 || lower[1] - seam[1] < 0.001) return undefined;
  return seam[0] / 1000;
}

/** Moves the lower face's weight from the head to the jaw, once per shared geometry. */
function weightJaw(
  mesh: THREE.SkinnedMesh,
  headIndex: number,
  jawIndex: number,
  hinge: THREE.Vector3,
  lipY: number,
): void {
  const g = mesh.geometry;
  if (g.userData.jawIndex === jawIndex) return;
  g.userData.jawIndex = jawIndex;
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const si = g.getAttribute('skinIndex') as THREE.BufferAttribute;
  const sw = g.getAttribute('skinWeight') as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    // The head's share of this vertex, and a free slot (or the smallest) for the jaw.
    let headSlot = -1;
    let headW = 0;
    let freeSlot = -1;
    let minW = Infinity;
    for (let k = 0; k < 4; k++) {
      const w = sw.getComponent(i, k);
      if (si.getComponent(i, k) === headIndex && w > 0) {
        headSlot = k;
        headW = w;
      } else if (w < minW) {
        minW = w;
        freeSlot = k;
      }
    }
    if (headSlot < 0 || headW < 0.5 || freeSlot < 0) continue;
    v.fromBufferAttribute(pos, i).applyMatrix4(mesh.bindMatrix);
    // Below the lip line (sharp, so the lips part) and in front of the hinge (soft, so the
    // jaw line blends into the cheek).
    const share = jawShare(v.y, v.z, hinge, lipY);
    if (share <= 0.001) continue;
    const toJaw = headW * share;
    // The jaw takes the free slot; whatever small weight that slot held goes to the head, so
    // the four weights still sum to one.
    sw.setComponent(i, headSlot, headW - toJaw + Math.max(0, minW));
    si.setComponent(i, freeSlot, jawIndex);
    sw.setComponent(i, freeSlot, toJaw);
  }
  si.needsUpdate = true;
  sw.needsUpdate = true;
}

/** How far the jaw takes a point of the face (bind pose): below the lip line, in front. */
function jawShare(y: number, z: number, hinge: THREE.Vector3, lipY: number): number {
  const below = 1 - smooth(lipY - 0.002, lipY + 0.002, y);
  const front = smooth(hinge.z + 0.015, hinge.z + 0.06, z);
  return below * front;
}

/** Mouth opening from a voice level (0..1), smoothed so it reads as speech, not flicker. */
export class MouthDriver {
  private open = 0;

  constructor(private readonly jaw: Jaw) {}

  /** `level` is the voice's loudness now (0..1). */
  update(level: number, dt: number): void {
    const target = level > 0.04 ? THREE.MathUtils.clamp(0.25 + level * 1.4, 0, 1) : 0;
    // Opens fast, closes a little slower, like a jaw.
    const rate = target > this.open ? 28 : 16;
    this.open += (target - this.open) * Math.min(1, rate * dt);
    this.jaw.set(this.open);
  }
}

/**
 * A speaking mouth without a level to follow (remote users whose loudness is unknown): a
 * syllable rhythm while `speaking` is true.
 */
export function syllables(t: number, phase: number): number {
  const s = Math.abs(Math.sin(t * 10.5 + phase)) * (0.55 + 0.45 * Math.sin(t * 2.7 + phase * 3));
  return THREE.MathUtils.clamp(s, 0, 1);
}
